import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import { create, update } from 'apis/researchFields.api';
import { createResearchFieldValidationSchema } from 'features/ResearchField/validation';
import { TextareaInput, TextInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(createResearchFieldValidationSchema) };

const Form = forwardRef(({ refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [researchFieldControl, setResearchFieldControl] = useState(null);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = useForm({ ...formOptions, mode: 'onChange' });

  const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(create);
  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(update);

  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_researchFieldControl) => {
        setResearchFieldControl(_researchFieldControl);
        setVisible(true);
        if (_researchFieldControl.mode === 'CREATE') {
          reset({});
        } else if (_researchFieldControl.mode === 'UPDATE') {
          reset(_researchFieldControl.value);
        }
      },
    }),
    []
  );

  const onSubmit = (data) => {
    if (researchFieldControl.mode === 'CREATE') {
      mutateCreate(data, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('researchField.create.createSuccess'));
          reset();
          refetch();
          setVisible(false);
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    } else {
      const newData = Object.keys(dirtyFields)?.reduce((resultObj, current) => {
        // eslint-disable-next-line no-param-reassign
        resultObj[current] = data[current];
        return resultObj;
      }, {});

      mutateUpdate(
        { id: data?.id, data: newData },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('researchField.update.updateSuccess'));
            refetch();
            setVisible(false);
          },
          onError: (error) => {
            toast(TOAST.ERROR, mapError(t, error));
          },
        }
      );
    }
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion

  return (
    <Dialog
      header={
        researchFieldControl?.mode === 'CREATE'
          ? t('researchField.create.title')
          : t('researchField.update.title')
      }
      visible={visible}
      style={{ maxWidth: '700px' }}
      draggable={false}
      onHide={() => setVisible(false)}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={checkKeyDown}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid">
            <div className="col-12">
              <TextInput
                label={t('researchField.name')}
                name="name"
                autoFocus
                control={control}
                errors={errors}
                isRequired
              />
            </div>

            <div className="col-12">
              <TextareaInput
                label={t('researchField.englishName')}
                name="englishName"
                control={control}
                errors={errors}
                isRequired
              />
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-6">
              <Button
                label={t('researchField.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset();
                  setVisible(false);
                }}
              />
            </div>
            <div className="col-6">
              <Button
                label={
                  researchFieldControl?.mode === 'CREATE'
                    ? t('researchField.add')
                    : t('researchField.save')
                }
                icon="pi pi-pencil"
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                onClick={checkFormValid}
                disabled={!isDirty || !(Object.keys(dirtyFields)?.length > 0)}
              />
            </div>
          </div>
        </form>
        {(isLoadingCreate || isLoadingUpdate) && <Loading />}
      </div>
    </Dialog>
  );
});

export default Form;

Form.propTypes = {
  refetch: PropTypes.func.isRequired,
};
